import React, { useState } from 'react';
import { Card } from '../ui/card';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import { Button } from '../ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../ui/carousel';
import { Dialog, DialogContent } from '../ui/dialog';
import { Volume2, VolumeX, Play } from 'lucide-react';

// Helper to get Cloudflare Stream video ID from URL
const getStreamVideoId = (url) => {
  if (!url) return null;
  // Handle iframe URL: https://iframe.cloudflarestream.com/{video_id}
  const iframeMatch = url.match(/iframe\.cloudflarestream\.com\/([a-zA-Z0-9]+)/);
  if (iframeMatch) return iframeMatch[1];
  // Handle customer URL: https://customer-{id}.cloudflarestream.com/{video_id}/watch
  const customerMatch = url.match(/cloudflarestream\.com\/([a-zA-Z0-9]+)/);
  if (customerMatch) return customerMatch[1];
  return null;
};

// Get auto-generated thumbnail from Cloudflare Stream
const getStreamThumbnail = (videoUrl, time = '0s') => {
  const videoId = getStreamVideoId(videoUrl);
  if (videoId) {
    return `https://videodelivery.net/${videoId}/thumbnails/thumbnail.jpg?time=${time}&height=480`;
  }
  return null;
};

export const ReelShowcaseCarousel = ({ reels }) => {
  const [selectedReel, setSelectedReel] = useState(null);

  if (!reels || reels.length === 0) return null;

  return (
    <>
      <div className="w-full" data-testid="reel-showcase-carousel">
        <Carousel className="w-full">
          <CarouselContent className="-ml-2 md:-ml-4">
            {reels.map((reel, index) => {
              // Use auto-thumbnail from Cloudflare if custom thumbnail is missing or broken
              const thumbnailUrl = reel.thumbnail_url || getStreamThumbnail(reel.video_url, '1s');
              
              return (
                <CarouselItem key={reel.id} className="pl-2 md:pl-4 basis-1/2 md:basis-1/3 lg:basis-1/4">
                  <div
                    className="cursor-pointer"
                    onClick={() => setSelectedReel(reel)}
                    data-testid="reel-showcase-slide"
                  >
                    <Card className="overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-shadow duration-200">
                      <AspectRatio ratio={9/16} className="bg-gray-200">
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt={reel.title}
                            className="object-cover w-full h-full"
                            onError={(e) => {
                              // Fallback to Cloudflare auto-thumbnail
                              const autoThumb = getStreamThumbnail(reel.video_url, '1s');
                              if (autoThumb && e.target.src !== autoThumb) {
                                e.target.src = autoThumb;
                              }
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
                            <Play className="w-12 h-12 text-primary" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/10 flex items-center justify-center hover:bg-black/20 transition-colors">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                            <Play className="w-6 h-6 text-black ml-1" fill="currentColor" />
                          </div>
                        </div>
                      </AspectRatio>
                      <div className="p-2">
                        <p className="text-xs font-medium line-clamp-2">{reel.title}</p>
                      </div>
                    </Card>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </Carousel>
      </div>

      <Dialog open={!!selectedReel} onOpenChange={() => setSelectedReel(null)}>
        <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="reel-showcase-dialog">
          {selectedReel && (
            <div className="relative">
              <AspectRatio ratio={9/16} className="bg-black">
                {/* Use Cloudflare Stream iframe for proper playback */}
                {getStreamVideoId(selectedReel.video_url) ? (
                  <iframe
                    src={`https://iframe.cloudflarestream.com/${getStreamVideoId(selectedReel.video_url)}?autoplay=true&loop=true&muted=false&preload=auto`}
                    className="w-full h-full"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    title={selectedReel.title}
                  />
                ) : (
                  <video
                    src={selectedReel.video_url}
                    className="w-full h-full object-contain"
                    controls
                    autoPlay
                    loop
                    playsInline
                  />
                )}
              </AspectRatio>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent text-white pointer-events-none">
                <h3 className="font-semibold mb-2">{selectedReel.title}</h3>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};